import React, { useState } from 'react';
import { useGMB } from '../contexts/GMBContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toolsAPI } from '../lib/api';
import { 
  BarChart3, AlertTriangle, CheckCircle, AlertCircle, Lightbulb,
  Star, MapPin, Phone, Globe, Clock, Camera, RefreshCw,
  TrendingUp, TrendingDown, Minus
} from 'lucide-react';
import { toast } from 'sonner';

const AuditPage = () => {
  const { t, language } = useLanguage();
  const { gmbProfile, isConnected, refreshProfile } = useGMB();
  
  const [loading, setLoading] = useState(false);
  const [customAudit, setCustomAudit] = useState(null);
  const [showCustomForm, setShowCustomForm] = useState(false);
  
  const [formData, setFormData] = useState({
    business_name: '',
    category: '',
    description: '',
    city: '',
    language: language === 'hi' ? 'Hindi' : 'English'
  });

  const handleCustomAudit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await toolsAPI.runAudit(formData);
      setCustomAudit(response.data.audit);
      toast.success('Audit completed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run audit');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-[#1E8E3E]';
    if (score >= 60) return 'text-[#F9AB00]';
    return 'text-[#D93025]';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-[#E6F4EA]';
    if (score >= 60) return 'bg-[#FEF7E0]';
    return 'bg-[#FCE8E6]';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    return 'Needs Work';
  };

  const getSeverityConfig = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high':
        return { 
          color: 'text-[#D93025]', 
          bg: 'bg-[#FCE8E6]', 
          border: 'border-[#F5C6CB]', 
          icon: <AlertTriangle className="w-5 h-5" />,
          label: 'High Priority'
        };
      case 'medium':
        return { 
          color: 'text-[#F9AB00]', 
          bg: 'bg-[#FEF7E0]', 
          border: 'border-[#FFE69C]', 
          icon: <AlertCircle className="w-5 h-5" />,
          label: 'Medium Priority'
        };
      default:
        return { 
          color: 'text-[#1A73E8]', 
          bg: 'bg-[#E8F0FE]', 
          border: 'border-[#B6D4FE]', 
          icon: <Lightbulb className="w-5 h-5" />,
          label: 'Suggestion'
        };
    }
  };

  // Use connected profile audit or custom audit
  const auditData = customAudit || (isConnected && gmbProfile ? {
    score: gmbProfile.auditScore,
    issues: gmbProfile.auditIssues,
    recommendations: gmbProfile.suggestions
  } : null);

  const profileData = isConnected && gmbProfile ? gmbProfile : null;

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-[#FEF7E0] rounded-xl flex items-center justify-center">
            <BarChart3 className="w-6 h-6 text-[#F9AB00]" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
              Profile Optimization Audit
            </h1>
            <p className="text-[#5F6368]">Analyze and improve your Google Business Profile</p>
          </div>
        </div>
        {isConnected && (
          <button
            onClick={() => setShowCustomForm(!showCustomForm)}
            className="btn-outline text-sm"
          >
            {showCustomForm ? 'View Connected Profile' : 'Audit Different Profile'}
          </button>
        )}
      </div>

      {/* Show custom audit form if requested or not connected */}
      {(showCustomForm || !isConnected) && (
        <div className="dashboard-card">
          <h3 className="text-lg font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>
            Enter Profile Details
          </h3>
          <form onSubmit={handleCustomAudit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#202124] mb-2">
                  Business Name
                </label>
                <input
                  type="text"
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  className="input-google"
                  placeholder="e.g., Sunrise Coffee House"
                  required
                  data-testid="audit-business-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#202124] mb-2">
                  Category
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="input-google"
                  placeholder="e.g., Coffee Shop"
                  required
                  data-testid="audit-category-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#202124] mb-2">
                Business Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-google min-h-[100px]"
                placeholder="Enter your current GMB description..."
                required
                data-testid="audit-description-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#202124] mb-2">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="input-google"
                  placeholder="e.g., Mumbai"
                  required
                  data-testid="audit-city-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#202124] mb-2">Language</label>
                <select
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  className="input-google"
                  data-testid="audit-language-select"
                >
                  <option value="English">English</option>
                  <option value="Hindi">Hindi</option>
                </select>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-google flex items-center justify-center gap-2"
              data-testid="run-audit-btn"
            >
              {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <BarChart3 className="w-5 h-5" />}
              {loading ? 'Analyzing...' : 'Run Audit'}
            </button>
          </form>
        </div>
      )}

      {/* Connected Profile Display */}
      {profileData && !showCustomForm && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Overview */}
          <div className="dashboard-card">
            <div className="flex items-center gap-4 mb-6">
              <img 
                src={profileData.profileImage} 
                alt={profileData.name}
                className="w-16 h-16 rounded-xl object-cover"
              />
              <div>
                <h3 className="font-semibold text-[#202124]">{profileData.name}</h3>
                <p className="text-sm text-[#5F6368]">{profileData.category}</p>
                <div className="flex items-center gap-1 mt-1">
                  <Star className="w-4 h-4 fill-[#F9AB00] text-[#F9AB00]" />
                  <span className="text-sm font-medium">{profileData.rating}</span>
                  <span className="text-sm text-[#5F6368]">({profileData.reviewCount})</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <MapPin className="w-4 h-4 text-[#5F6368]" />
                <span className="text-[#202124]">{profileData.address}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Phone className="w-4 h-4 text-[#5F6368]" />
                <span className="text-[#202124]">{profileData.phone}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Globe className="w-4 h-4 text-[#5F6368]" />
                <span className="text-[#1A73E8]">{profileData.website}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Camera className="w-4 h-4 text-[#5F6368]" />
                <span className="text-[#202124]">{profileData.photos} photos</span>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-[#DADCE0]">
              <p className="text-xs text-[#5F6368] mb-2">ATTRIBUTES</p>
              <div className="flex flex-wrap gap-2">
                {profileData.attributes.map((attr, i) => (
                  <span key={i} className="px-2 py-1 bg-[#F1F3F4] text-[#5F6368] text-xs rounded">
                    {attr}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Score Card */}
          <div className="dashboard-card flex flex-col items-center justify-center text-center">
            <p className="text-sm text-[#5F6368] mb-2">OPTIMIZATION SCORE</p>
            <div className={`w-32 h-32 rounded-full ${getScoreBg(auditData.score)} flex items-center justify-center mb-4`}>
              <span className={`text-5xl font-bold ${getScoreColor(auditData.score)}`} style={{ fontFamily: 'Outfit' }}>
                {auditData.score}
              </span>
            </div>
            <p className={`text-lg font-semibold ${getScoreColor(auditData.score)}`}>
              {getScoreLabel(auditData.score)}
            </p>
            <p className="text-sm text-[#5F6368] mt-2">
              {auditData.score >= 80 ? 'Your profile is well optimized!' : 
               auditData.score >= 60 ? 'Some improvements recommended' :
               'Significant improvements needed'}
            </p>
          </div>

          {/* Quick Stats */}
          <div className="space-y-4">
            <div className="dashboard-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5F6368]">Profile Completeness</p>
                  <p className="text-2xl font-semibold text-[#202124]">85%</p>
                </div>
                <TrendingUp className="w-8 h-8 text-[#1E8E3E]" />
              </div>
              <div className="mt-3 w-full h-2 bg-[#DADCE0] rounded-full overflow-hidden">
                <div className="h-full bg-[#1E8E3E] rounded-full" style={{ width: '85%' }} />
              </div>
            </div>

            <div className="dashboard-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5F6368]">Search Visibility</p>
                  <p className="text-2xl font-semibold text-[#202124]">72%</p>
                </div>
                <Minus className="w-8 h-8 text-[#F9AB00]" />
              </div>
              <div className="mt-3 w-full h-2 bg-[#DADCE0] rounded-full overflow-hidden">
                <div className="h-full bg-[#F9AB00] rounded-full" style={{ width: '72%' }} />
              </div>
            </div>

            <div className="dashboard-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5F6368]">Review Response Rate</p>
                  <p className="text-2xl font-semibold text-[#202124]">45%</p>
                </div>
                <TrendingDown className="w-8 h-8 text-[#D93025]" />
              </div>
              <div className="mt-3 w-full h-2 bg-[#DADCE0] rounded-full overflow-hidden">
                <div className="h-full bg-[#D93025] rounded-full" style={{ width: '45%' }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Issues & Recommendations */}
      {auditData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Issues */}
          <div className="dashboard-card">
            <h3 className="text-lg font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>
              Issues Found ({auditData.issues?.length || 0})
            </h3>
            <div className="space-y-3">
              {auditData.issues?.map((issue, index) => {
                const config = getSeverityConfig(issue.severity);
                return (
                  <div key={index} className={`p-4 rounded-lg border ${config.border} ${config.bg}`}>
                    <div className="flex items-start gap-3">
                      <span className={config.color}>{config.icon}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold uppercase ${config.color}`}>
                            {config.label}
                          </span>
                        </div>
                        <h4 className="font-medium text-[#202124]">{issue.title}</h4>
                        <p className="text-sm text-[#5F6368] mt-1">{issue.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recommendations */}
          <div className="dashboard-card">
            <h3 className="text-lg font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>
              Recommendations
            </h3>
            <div className="space-y-3">
              {auditData.recommendations?.map((rec, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-[#F8F9FA] rounded-lg hover:bg-[#F1F3F4] transition-colors">
                  <CheckCircle className="w-5 h-5 text-[#1E8E3E] flex-shrink-0 mt-0.5" />
                  <p className="text-[#202124]">{rec}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditPage;
